// modules/reporteFacturas/pages/ReporteFacturasMoraPage.jsx
// Migracion de la rama $cin de busqueda.php ("Reporte por Factura en Mora")
// — a diferencia del resto de reportes de /reporte-facturas, no filtra por
// mes/año (muestra todo sin importar las fechas): cualquier factura o
// pre-factura activa con saldo pendiente, sea de cuando sea.
import React, { useMemo, useState } from "react";
import {
  Box, Button, TextField, MenuItem, Typography, Alert, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
  TableSortLabel, Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import { useAuth } from "../../../context/AuthContext";
import { getReporteFacturasMora, exportarReporteFacturasMora } from "../services/reporteFacturasService";
import { imprimirFactura } from "../../facturacion/services/facturacionService";

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.72rem",
  whiteSpace: "nowrap",
  padding: "3px 6px",
  backgroundColor: theme.palette.mode === "dark"
    ? theme.palette.grey[800] : theme.palette.grey[100],
}));

const compactFieldSx = {
  "& .MuiInputBase-root": { minHeight: 32, fontSize: "0.8rem" },
  "& .MuiInputBase-input": { py: 0.65 },
  "& .MuiInputLabel-root": { fontSize: "0.78rem" },
  "& .MuiSelect-select": { py: "6px" },
  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
};

const SUCURSALES = ["Todo", "La Paz", "Cochabamba", "Santa Cruz"];

const CATEGORIAS = [
  { key: 1, label: "Serv Tecnico + Alquiler" },
  { key: 2, label: "Rastreo Internacional" },
  { key: 3, label: "Rastreo Nacional" },
  { key: 4, label: "Descuentos" },
  { key: 5, label: "Ventas" },
  { key: 6, label: "Alquiler Radio" },
  { key: 7, label: "Servicio de comunicacion" },
];

const normalizarSucursal = (value) => {
  const s = String(value || "").trim().toLowerCase();
  if (s.includes("santa cruz")) return "Santa Cruz";
  if (s.includes("cochabamba")) return "Cochabamba";
  if (s.includes("la paz") || s.includes("el alto")) return "La Paz";
  return "Todo";
};

const formatMoneda = (value) =>
  Number(value || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatFecha = (value) => {
  const m = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return "-";
  return `${m[3]}/${m[2]}/${m[1]}`;
};

const COLUMNAS = [
  { col: "grupo", label: "Grupo" },
  { col: "nombre", label: "Cliente" },
  { col: "centralRiesgo", label: "Central de Riesgo" },
  { col: "serv", label: "Servicio" },
  { col: "descrip", label: "Descripción" },
  { col: "fac", label: "N° Factura" },
  { col: "sucursal_facturar", label: "Sucursal" },
  { col: "coddos", label: "N° Pre-Factura" },
  { col: "fecha_pre_factura", label: "Fecha Pre-Factura" },
  { col: "pre_fac", label: "Es Pre-Factura" },
];

const COLUMNAS_FINALES = [
  { col: "cantidad_detalle", label: "Cantidad", align: "right" },
  { col: "fecha", label: "Fecha Factura" },
  { col: "mes_servicio", label: "Mes de Servicio" },
  { col: "bolivianos", label: "Monto Bs", align: "right" },
  { col: "dolares", label: "Monto $us", align: "right" },
  { col: "saldo", label: "Saldo", align: "right" },
  { col: "pagada", label: "Cobrada" },
  { col: "fecha_primer_pago", label: "Fecha Primer Pago" },
];

const TOTAL_COLUMNAS = COLUMNAS.length + CATEGORIAS.length + COLUMNAS_FINALES.length;

const getValorOrdenable = (row, col) => {
  if (col.startsWith("cat")) return Number(row.detalle?.[Number(col.slice(3))]) || 0;
  if (col === "fecha" || col === "fecha_pre_factura" || col === "fecha_primer_pago") {
    return row[col] ? new Date(row[col]).getTime() : 0;
  }
  return row[col];
};

const compararValores = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === "string" || typeof b === "string") {
    return String(a).localeCompare(String(b), "es", { numeric: true, sensitivity: "base" });
  }
  return a - b;
};

const SortableHeaderCell = ({ col, label, align, orderBy, orderDir, onSort }) => (
  <StyledHeaderCell align={align} sortDirection={orderBy === col ? orderDir : false}>
    <TableSortLabel
      active={orderBy === col}
      direction={orderBy === col ? orderDir : "asc"}
      onClick={() => onSort(col)}
    >
      {label}
    </TableSortLabel>
  </StyledHeaderCell>
);

const ReporteFacturasMoraPage = () => {
  const { user } = useAuth();
  const [sucursal, setSucursal] = useState(() => normalizarSucursal(user?.sucursal));
  const [items, setItems] = useState([]);
  const [totales, setTotales] = useState({ bolivianos: 0, dolares: 0 });
  const [buscado, setBuscado] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [exportando, setExportando] = useState(false);
  const [abriendoCod, setAbriendoCod] = useState(null);
  // Por defecto ordenado por Saldo descendente (mayor deuda primero) — tiene
  // mas sentido para un reporte de mora que el orden crudo del backend
  // (alfabetico por cliente).
  const [orderBy, setOrderBy] = useState("saldo");
  const [orderDir, setOrderDir] = useState("desc");

  const handleSort = (col) => {
    if (orderBy === col) {
      setOrderDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(col);
      setOrderDir("asc");
    }
  };

  const itemsOrdenados = useMemo(() => {
    if (!orderBy) return items;
    const copia = [...items].sort((a, b) =>
      compararValores(getValorOrdenable(a, orderBy), getValorOrdenable(b, orderBy))
    );
    if (orderDir === "desc") copia.reverse();
    return copia;
  }, [items, orderBy, orderDir]);

  const handleBuscar = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await getReporteFacturasMora({ sucursal });
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotales(data?.totales || { bolivianos: 0, dolares: 0 });
      setBuscado(true);
    } catch (e) {
      setItems([]);
      setError(e?.response?.data?.error || "No se pudo generar el reporte.");
    } finally {
      setLoading(false);
    }
  };

  const handleExportar = async () => {
    setExportando(true);
    setError("");
    try {
      const response = await exportarReporteFacturasMora({ sucursal });
      const url = URL.createObjectURL(new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "reporte_facturas_mora.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo exportar el reporte.");
    } finally {
      setExportando(false);
    }
  };

  const handleAbrirFactura = async (cod) => {
    setAbriendoCod(cod);
    try {
      const response = await imprimirFactura(cod);
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch {
      setError("No se pudo abrir la factura.");
    } finally {
      setAbriendoCod(null);
    }
  };

  return (
    <DashboardLayout>
      <PageHeader
        title="Reporte por Factura en Mora"
        subtitle="Facturas y pre-facturas activas con saldo pendiente, sin importar la fecha"
        backTo="/reporte-facturas"
      />
      <Box sx={{ p: 1 }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1, flexWrap: "wrap" }}>
          <TextField
            select size="small" label="Sucursal"
            sx={{ ...compactFieldSx, width: 150 }}
            value={sucursal}
            onChange={(e) => setSucursal(e.target.value)}
          >
            {SUCURSALES.map((s) => (
              <MenuItem key={s} value={s} sx={{ fontSize: "0.85rem" }}>{s}</MenuItem>
            ))}
          </TextField>

          <Button
            size="small"
            variant="contained"
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />}
            onClick={handleBuscar}
            disabled={loading}
            sx={{ minHeight: 32 }}
          >
            {loading ? "Buscando..." : "Buscar"}
          </Button>

          {buscado && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              Total: {items.length}
            </Typography>
          )}

          <Box sx={{ flex: 1 }} />

          <Button
            size="small"
            variant="outlined"
            color="success"
            startIcon={exportando ? <CircularProgress size={14} color="inherit" /> : <FileDownloadIcon />}
            onClick={handleExportar}
            disabled={exportando || !buscado || items.length === 0}
            sx={{ minHeight: 32 }}
          >
            {exportando ? "Exportando..." : "Exportar Excel"}
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!buscado ? (
          <Alert severity="info">Elige sucursal y presiona Buscar.</Alert>
        ) : !loading && items.length === 0 ? (
          <Alert severity="info">No hay facturas en mora para la sucursal seleccionada.</Alert>
        ) : (
          <>
            <Paper variant="outlined">
              <TableContainer sx={{ maxHeight: "65vh" }}>
                <Table size="small" stickyHeader sx={{ "& td": { py: 0.35, px: 0.75, fontSize: "0.74rem", whiteSpace: "nowrap" } }}>
                  <TableHead>
                    <TableRow>
                      {COLUMNAS.map((c) => (
                        <SortableHeaderCell key={c.col} {...c} orderBy={orderBy} orderDir={orderDir} onSort={handleSort} />
                      ))}
                      {CATEGORIAS.map((c) => (
                        <SortableHeaderCell
                          key={c.key} col={`cat${c.key}`} label={c.label} align="right"
                          orderBy={orderBy} orderDir={orderDir} onSort={handleSort}
                        />
                      ))}
                      {COLUMNAS_FINALES.map((c) => (
                        <SortableHeaderCell key={c.col} {...c} orderBy={orderBy} orderDir={orderDir} onSort={handleSort} />
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={TOTAL_COLUMNAS} align="center" sx={{ py: 3 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : (
                      itemsOrdenados.map((r) => (
                        <TableRow key={r.cod} hover>
                          <TableCell>{r.grupo || "-"}</TableCell>
                          <TableCell sx={{ maxWidth: 200 }}>
                            <Button
                              size="small"
                              variant="text"
                              disabled={abriendoCod === r.cod}
                              onClick={() => handleAbrirFactura(r.cod)}
                              title={r.nombre}
                              sx={{
                                fontSize: "0.74rem", textTransform: "none", p: 0, minWidth: 0, minHeight: 0,
                                justifyContent: "flex-start", display: "block", maxWidth: "100%",
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                              }}
                            >
                              {abriendoCod === r.cod ? "Abriendo..." : r.nombre}
                            </Button>
                          </TableCell>
                          <TableCell>
                            {r.centralRiesgo === "Cliente Reportado" ? (
                              <Chip size="small" label={r.centralRiesgo} color="error" variant="filled" sx={{ fontSize: "0.68rem", height: 20 }} />
                            ) : (
                              r.centralRiesgo || "-"
                            )}
                          </TableCell>
                          <TableCell>{r.serv}</TableCell>
                          <TableCell>{r.descrip || "-"}</TableCell>
                          <TableCell>{r.fac}</TableCell>
                          <TableCell>{r.sucursal_facturar}</TableCell>
                          <TableCell>{r.coddos || "-"}</TableCell>
                          <TableCell>{formatFecha(r.fecha_pre_factura)}</TableCell>
                          <TableCell>{r.pre_fac === "si" ? "Sí" : "No"}</TableCell>
                          {CATEGORIAS.map((c) => (
                            <TableCell key={c.key} align="right">{formatMoneda(r.detalle?.[c.key])}</TableCell>
                          ))}
                          <TableCell align="right">{r.cantidad_detalle}</TableCell>
                          <TableCell>{formatFecha(r.fecha)}</TableCell>
                          <TableCell>{r.mes_servicio}</TableCell>
                          <TableCell align="right">{formatMoneda(r.bolivianos)}</TableCell>
                          <TableCell align="right">{formatMoneda(r.dolares)}</TableCell>
                          <TableCell align="right">{formatMoneda(r.saldo)}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={r.pagada === "si" ? "Cobrada" : "Pendiente"}
                              color={r.pagada === "si" ? "success" : "warning"}
                              variant={r.pagada === "si" ? "filled" : "outlined"}
                              sx={{ fontSize: "0.68rem", height: 20 }}
                            />
                          </TableCell>
                          <TableCell>{r.fecha_primer_pago ? formatFecha(r.fecha_primer_pago) : "Sin pago"}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <Paper variant="outlined" sx={{ mt: 1.5, p: 1.5, maxWidth: 320, ml: "auto" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Total bolivianos</Typography>
                <Typography variant="body2" fontWeight={700}>{formatMoneda(totales.bolivianos)}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                <Typography variant="body2">Total dólares</Typography>
                <Typography variant="body2" fontWeight={700}>{formatMoneda(totales.dolares)}</Typography>
              </Box>
            </Paper>
          </>
        )}
      </Box>
    </DashboardLayout>
  );
};

export default ReporteFacturasMoraPage;
